import { ChevronRight, ExternalLink } from 'lucide-react';
import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogPanel,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Separator } from '@/components/atoms/separator';
import { MetricToggle } from '@/components/molecules/MetricToggle';
import { useBelowWidth } from '@/hooks/ui/useBelowWidth';
import { cn } from '@/libs/utils/cn';
import { useMixpanel } from '@/hooks/useMixpanel';
import { BUTTON_CLICKED } from '@/libs/mixpanel';
import { formatDistance, formatNumber } from '@/libs/utils/format';
import type {
  ICapturedAadtExtracted,
  IHighestPerformingCompetitor,
  IProjectResults,
} from '@/types/api';
import { MAP_DETAIL_POPUP_BELOW_PX, METRICS_LIST } from '../constants';
import { getGoogleMapStreetViewUrl } from '../utils/getGoogleMapStreetViewUrl';
import type { MapLayersService } from '../utils/MapLayersService';
import { pointOfInterestPlace } from '../utils/placePayload';
import { PlaceMark } from './PlaceMark';
import { MetricItem } from './MetricItem';
import { BaseMetrics } from './metrics/BaseMetrics';
import { ForecastedMetrics } from './metrics/ForecastedMetrics';

interface AnalyticsContext {
  projectId?: string;
}

interface MarkerMetricsBodyProps {
  locationData: IProjectResults;
  capturedAadtData?: ICapturedAadtExtracted;
  layersService?: MapLayersService;
  analyticsContext?: AnalyticsContext;
  /** Optional control rendered between the street-view row and the metrics (e.g. the Map radius slider). */
  radiusControl?: ReactNode;
  /** The side metrics panel. Deal cards turn this off on the deal face. */
  showExtension?: boolean;
  /** Border, radius and shadow of the card the panel continues, when not the dialog's own. */
  panelClassName?: string;
}

export function MarkerMetricsBody({
  locationData,
  capturedAadtData,
  layersService,
  analyticsContext,
  radiusControl,
  showExtension = true,
  panelClassName,
}: MarkerMetricsBodyProps) {
  const projectId = analyticsContext?.projectId ?? '';

  const { location, metrics, forecast } = locationData;
  const place = locationData.point_of_interest;
  const [selectedMetric, setSelectedMetric] = useState<'forecast' | 'keyMetrics'>('keyMetrics');
  const { value: locationDistance, unit: distanceUnit } = useMemo(
    () => formatDistance(location.radius),
    [location.radius],
  );
  const gmapURI = getGoogleMapStreetViewUrl(
    location.coordinates.latitude,
    location.coordinates.longitude,
  );

  const trackButtonClicked = useMixpanel((state) => state.trackButtonClicked);

  return (
    <>
      <DialogTitle className="hidden"></DialogTitle>
      <DialogDescription className="hidden"></DialogDescription>

      <div className="flex flex-col">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-col gap-1.5 mb-2">
            {/* A location made from a place is named after it, with the address under it like a deal. */}
            {place ? (
              <>
                <div className="flex items-center gap-2">
                  <PlaceMark place={pointOfInterestPlace(place)} />
                  <span className="min-w-0 truncate text-[15px] font-semibold leading-5 text-primary-600">
                    {place.name}
                  </span>
                </div>
                <div className="text-[11px] leading-[15px] text-[#626968]">{location.address}</div>
              </>
            ) : (
              <div className="text-sm text-primary-600">{location.address}</div>
            )}
            <a
              href={gmapURI}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-800 hover:underline flex items-center gap-1 text-sm font-medium"
            >
              See street view
              <ExternalLink size={16} strokeWidth={2.5} />
            </a>
          </div>
        </div>
        <Separator className="mb-1.5" />
        {radiusControl && (
          <>
            {radiusControl}
            <Separator className="my-1.5" />
          </>
        )}
        <MetricToggle
          value={selectedMetric}
          onValueChange={(value) => {
            if (!value) return;
            setSelectedMetric(value);
            if (value === 'forecast') {
              trackButtonClicked(BUTTON_CLICKED.FORECAST_SECTION, { project_id: projectId });
            }
          }}
          className="mb-3.5"
          forecastDisabled={!forecast}
        />
        <div className="h-fit max-h-[500px] overflow-y-auto">
          {selectedMetric === 'forecast' && forecast !== null ? (
            <ForecastedMetrics forecast={forecast} limit={2} projectId={projectId} />
          ) : (
            <BaseMetrics
              metrics={metrics}
              capturedAadtData={capturedAadtData}
              layersService={layersService}
              locationDistance={locationDistance}
              distanceUnit={distanceUnit}
              projectId={projectId}
            />
          )}
        </div>
      </div>
      {showExtension && selectedMetric === 'forecast' && forecast && (
        <ExtraMetrics title="More demand factors" className={panelClassName}>
          <ForecastedMetrics forecast={forecast} offset={2} hideHeader projectId={projectId} />
        </ExtraMetrics>
      )}
      {showExtension && selectedMetric === 'keyMetrics' && (
        <ExtraMetrics
          title={`More metrics within ${locationDistance} ${distanceUnit} radius`}
          className={panelClassName}
        >
          <MetricItem
            label="AADT - sum"
            metricKey={METRICS_LIST.SUM_AADT}
            metrics={metrics}
            formatValue={formatNumber}
          />
          <Separator />
          <MetricItem
            label="Total HPC stations within radius"
            metricKey={METRICS_LIST.NUMBER_HPC_STATIONS}
            metrics={metrics}
            formatValue={formatNumber}
          />
          <Separator />
          <MetricItem
            label="Avg. charges/station/day (other CPOs)"
            metricKey={METRICS_LIST.AVERAGE_CHARGES_STATION_PER_DAY}
            metrics={metrics}
            formatValue={(value: number) => value.toFixed(1)}
          />
          {locationData.highest_performing_competitor && (
            <>
              <Separator />
              <HighestPerformingCompetitorMetric
                competitor={locationData.highest_performing_competitor}
              />
            </>
          )}
        </ExtraMetrics>
      )}
    </>
  );
}

function HighestPerformingCompetitorMetric({
  competitor,
}: {
  competitor: IHighestPerformingCompetitor;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex justify-between gap-2">
        <span className="text-sm text-muted-foreground">Highest performing competitor</span>
        <span className="text-sm font-bold text-primary-700 text-right">{competitor.network}</span>
      </div>
      <span className="text-xs text-muted-foreground text-right">
        {competitor.performance.toFixed(1)} charges/station/day
      </span>
    </div>
  );
}

function ExtraMetrics({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) {
  const asPopup = useBelowWidth(MAP_DETAIL_POPUP_BELOW_PX);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);

  // The card body scrolls. Portaling onto the dialog keeps the tab and the panel outside that
  // scrollport, so they can stick out of the right edge.
  useLayoutEffect(() => {
    const dialog = anchorRef.current?.closest('[data-slot="dialog-content"]');
    if (dialog instanceof HTMLElement) setHost(dialog);
  }, []);

  const panel = asPopup ? (
    <>
      <button
        type="button"
        aria-label="Open panel"
        onClick={() => setOpen(true)}
        className="absolute bottom-4 -right-5 z-50 flex h-8 w-5 items-center justify-center rounded-r-md border border-l-0 border-border bg-background hover:bg-accent"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent size="sm" className="max-h-[min(80dvh,32rem)] overflow-y-auto">
            <DialogTitle className="pr-8 text-sm font-semibold text-primary-700">
              {title}
            </DialogTitle>
            <div className="mt-3 flex flex-col gap-2">{children}</div>
          </DialogContent>
        </Dialog>
      )}
    </>
  ) : (
    <DialogPanel
      side="right"
      buttonPosition="bottom"
      width="w-64"
      className={cn('overflow-y-auto p-3', className)}
    >
      <div className="flex flex-col gap-2">
        <h3 className="font-semibold text-sm text-primary-700">{title}</h3>
        <Separator />
        {children}
      </div>
    </DialogPanel>
  );

  return (
    <>
      <span ref={anchorRef} className="hidden" />
      {host ? createPortal(panel, host) : panel}
    </>
  );
}
